import { createRouter, type RouteLocationNormalized, type RouteRecordRaw, type RouterHistory } from 'vue-router'
import { useAuth, type Role } from '@/stores/auth'
import CoursesView from '@/views/CoursesView.vue'
import ForbiddenView from '@/views/ForbiddenView.vue'
import NotFoundView from '@/views/NotFoundView.vue'

declare module 'vue-router' {
    interface RouteMeta {
        /** Roles that may open the route; none means any logged-in user. */
        roles?: Role[]
    }
}

export const routes: RouteRecordRaw[] = [
    { path: '/', name: 'courses', component: CoursesView },
    // Final URLs of screens that later client slices build (spec D78).
    { path: '/schedules/:semester/:course/:type(lec|tut|sem|lab)', name: 'schedule', component: () => import('@/views/ScheduleView.vue') },
    {
        path: '/schedules/:semester/:course/:type(lec|tut|sem|lab)/edit',
        name: 'schedule-edit',
        component: () => import('@/views/ScheduleEditView.vue'),
        // An external edits the courses an admin assigned to them (D169); the API decides per course.
        meta: { roles: ['teacher', 'admin', 'external'] },
    },
    {
        path: '/projects/:semester',
        name: 'projects',
        component: () => import('@/views/ProjectsView.vue'),
        meta: { roles: ['teacher'] },
    },
    {
        path: '/statistics',
        name: 'statistics',
        component: () => import('@/views/PersonalStatisticsView.vue'),
        meta: { roles: ['teacher', 'admin', 'external'] },
    },
    {
        path: '/statistics/teachers',
        name: 'statistics-teachers',
        component: () => import('@/views/TeacherStatisticsView.vue'),
        meta: { roles: ['admin'] },
    },
    {
        path: '/statistics/courses',
        name: 'statistics-courses',
        component: () => import('@/views/CourseStatisticsView.vue'),
        meta: { roles: ['admin'] },
    },
    { path: '/externals', name: 'externals', component: () => import('@/views/ExternalsView.vue'), meta: { roles: ['admin'] } },
    { path: '/rewards', name: 'rewards', component: () => import('@/views/RewardsView.vue'), meta: { roles: ['rewards'] } },
    { path: '/forbidden', name: 'forbidden', component: ForbiddenView },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView },
]

export function roleGuard(to: RouteLocationNormalized) {
    const roles = to.meta.roles
    if (!roles || roles.length === 0) {
        return true
    }
    const auth = useAuth()
    return roles.some((role) => auth.hasRole(role)) ? true : { name: 'forbidden' }
}

export function createAppRouter(history: RouterHistory) {
    const router = createRouter({ history, routes })
    router.beforeEach(roleGuard)
    return router
}
